import type { CalendarDay } from "@/lib/clinic-clock";

import { CarePlanContext } from "./event";
import { PageParams } from "./pagination";
import { ProviderTiny } from "./provider";
import { Team } from "./team";

export enum TaskStatus {
  OPEN = "OPEN",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum TaskPriority {
  URGENT = "URGENT",
  HIGH = "HIGH",
  NORMAL = "NORMAL",
  LOW = "LOW",
}

export enum TaskPatientScope {
  PATIENT_SPECIFIC = "PATIENT_SPECIFIC",
  OPS = "OPS",
}

export interface PatientTiny {
  id: string;
  firstName?: string;
  lastName?: string;
}

export interface TaskCreate {
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  startDate?: CalendarDay;
  dueDate?: CalendarDay;
  patientId?: string;
  assigneeId?: string;
  teamId?: string;
  locationId?: string;
  /** A note this task acts on; auto-completed when that note is signed off. */
  targetNoteId?: string;
  /** A fax this task targets; the task deep-links to it ("Review fax" tasks). */
  targetFaxId?: string;
  /** A document this task acts on; auto-completed when that document is signed off. */
  targetDocumentId?: string;
  /**
   * The note an accepted AI scribe suggestion came from. Provenance and deep-link
   * only — unlike targetNoteId it is NOT swept when the note is signed off.
   */
  sourceNoteId?: string;
  /** The scribe suggestion this task was accepted from; flips its card to "Task created". */
  sourceTaskSuggestionId?: string;
}

export interface TaskUpdate {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  startDate?: CalendarDay | null;
  dueDate?: CalendarDay | null;
  patientId?: string | null;
  assigneeId?: string | null;
  teamId?: string | null;
  locationId?: string | null;
}

export interface TeamRef {
  id: string;
  name: string;
}

export interface TaskSummary {
  id: string;
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  startDate?: CalendarDay | null;
  dueDate?: CalendarDay | null;
  patientId?: string | null;
  /** Pursuit episode that generated this task, when task automation is opportunity-scoped. */
  opportunityId?: string | null;
  assigneeId?: string | null;
  teamId?: string | null;
  locationId?: string | null;
  targetNoteId?: string | null;
  /** A fax this task targets; the task deep-links to it ("Review fax" tasks). */
  targetFaxId?: string | null;
  /** A document this task acts on; auto-completed when that document is signed off. */
  targetDocumentId?: string | null;
  creatorId: string;
  completedById?: string | null;
  completedDate?: string | null;
  createdDate: string;
  updatedDate?: string | null;
  patient?: PatientTiny;
  assignee?: ProviderTiny;
  team?: TeamRef;
  creator?: ProviderTiny;
  carePlan?: CarePlanContext | null;
}

export interface LinkedCallRef {
  id: string;
  createdDate?: string | null;
}

export interface TaskCommentUpdate {
  content: string;
}

export interface TaskCommentAttachment {
  /** Signed link; empty while the comment is still uploading. */
  url: string;
  name: string;
}

export interface TaskCommentDetail {
  id: string;
  taskId: string;
  authorId: string;
  /** Empty when the comment is only attachments. */
  content: string;
  createdDate: string;
  updatedDate?: string | null;
  author?: ProviderTiny;
  attachments?: TaskCommentAttachment[];
}

interface SubscriberBase {
  id: string;
  createdDate: string;
}

export interface ProviderSubscriberDetail extends SubscriberBase {
  providerId: string;
  teamId?: never;
  provider?: ProviderTiny;
  team?: never;
}

export interface TeamSubscriberDetail extends SubscriberBase {
  providerId?: never;
  teamId: string;
  team?: TeamRef;
  provider?: never;
}

// Exactly one of providerId / teamId is set, matching the backend
// `ck_task_subscriber_exactly_one_target` invariant.
export type SubscriberDetail = ProviderSubscriberDetail | TeamSubscriberDetail;

export interface TaskDetail extends TaskSummary {
  comments: TaskCommentDetail[];
  subscribers: SubscriberDetail[];
  linkedCalls: LinkedCallRef[];
}

export interface TaskLinkedCounts {
  call: number;
}

export interface TaskCounts {
  total: number;
  linked: TaskLinkedCounts;
}

export interface TaskFetchParams extends PageParams {
  status?: TaskStatus[];
  priority?: TaskPriority[];
  assigneeId?: string;
  teamId?: string;
  patientId?: string;
  patientScope?: TaskPatientScope;
  creatorId?: string;
  subscriberId?: string;
  locationIds?: string[];
  includeUnassigned?: boolean;
  callId?: string;
  startDateFrom?: CalendarDay;
  startDateTo?: CalendarDay;
  dueDateFrom?: CalendarDay;
  dueDateTo?: CalendarDay;
  includeFuture?: boolean;
  search?: string;
}

// Activity Log Types
export enum TaskLogAction {
  CREATED = "CREATED",
  STATUS_CHANGED = "STATUS_CHANGED",
  PRIORITY_CHANGED = "PRIORITY_CHANGED",
  ASSIGNED = "ASSIGNED",
  DUE_DATE_CHANGED = "DUE_DATE_CHANGED",
  TITLE_CHANGED = "TITLE_CHANGED",
  DESCRIPTION_CHANGED = "DESCRIPTION_CHANGED",
  PATIENT_CHANGED = "PATIENT_CHANGED",
  START_DATE_CHANGED = "START_DATE_CHANGED",
  SUBSCRIBERS_CHANGED = "SUBSCRIBERS_CHANGED",
  COMPLETED = "COMPLETED",
  COMMENT_ADDED = "COMMENT_ADDED",
  TEAM_ASSIGNED = "TEAM_ASSIGNED",
}

interface TaskLogBase {
  id: string;
  taskId: string;
  actorId: string;
  actor?: ProviderTiny;
  createdDate: string;
}

interface TaskLogCreated extends TaskLogBase {
  actionType: TaskLogAction.CREATED;
  data?: undefined;
}

interface TaskLogStatusChanged extends TaskLogBase {
  actionType: TaskLogAction.STATUS_CHANGED;
  data: { oldStatus?: TaskStatus; newStatus: TaskStatus };
}

interface TaskLogPriorityChanged extends TaskLogBase {
  actionType: TaskLogAction.PRIORITY_CHANGED;
  data: { oldPriority?: TaskPriority; newPriority: TaskPriority };
}

interface TaskLogAssigned extends TaskLogBase {
  actionType: TaskLogAction.ASSIGNED;
  data: {
    oldAssigneeId?: string;
    oldAssigneeName?: string;
    newAssigneeId?: string;
    newAssigneeName?: string;
  };
}

interface TaskLogDueDateChanged extends TaskLogBase {
  actionType: TaskLogAction.DUE_DATE_CHANGED;
  data: { oldDueDate?: string; newDueDate?: string };
}

interface TaskLogTitleChanged extends TaskLogBase {
  actionType: TaskLogAction.TITLE_CHANGED;
  data: { oldTitle: string; newTitle: string };
}

interface TaskLogDescriptionChanged extends TaskLogBase {
  actionType: TaskLogAction.DESCRIPTION_CHANGED;
  data: { oldDescription?: string | null; newDescription?: string | null };
}

interface TaskLogPatientChanged extends TaskLogBase {
  actionType: TaskLogAction.PATIENT_CHANGED;
  data: {
    oldPatientId?: string | null;
    oldPatientName?: string | null;
    newPatientId?: string | null;
    newPatientName?: string | null;
  };
}

interface TaskLogStartDateChanged extends TaskLogBase {
  actionType: TaskLogAction.START_DATE_CHANGED;
  data: { oldStartDate?: string | null; newStartDate?: string | null };
}

interface TaskLogSubscribersChanged extends TaskLogBase {
  actionType: TaskLogAction.SUBSCRIBERS_CHANGED;
  data: { addedNames: string[]; removedNames: string[] };
}

interface TaskLogCompleted extends TaskLogBase {
  actionType: TaskLogAction.COMPLETED;
  data?: undefined;
}

interface TaskLogCommentAdded extends TaskLogBase {
  actionType: TaskLogAction.COMMENT_ADDED;
  data?: undefined;
}

interface TaskLogTeamAssigned extends TaskLogBase {
  actionType: TaskLogAction.TEAM_ASSIGNED;
  data: {
    teamId: string;
    teamName: string;
    resolvedAssigneeId?: string;
    resolvedAssigneeName?: string;
    resolutionMethod: string;
  };
}

export type TaskLogDetail =
  | TaskLogCreated
  | TaskLogStatusChanged
  | TaskLogPriorityChanged
  | TaskLogAssigned
  | TaskLogDueDateChanged
  | TaskLogTitleChanged
  | TaskLogDescriptionChanged
  | TaskLogPatientChanged
  | TaskLogStartDateChanged
  | TaskLogSubscribersChanged
  | TaskLogCompleted
  | TaskLogCommentAdded
  | TaskLogTeamAssigned;

// Task Settings Types
export enum TaskDefaultFilter {
  ASSIGNED_TO_ME = "ASSIGNED_TO_ME",
  SUBSCRIBED_TO = "SUBSCRIBED_TO",
  USER_CHOICE = "USER_CHOICE",
}

export enum TaskAssignmentStrategy {
  PATIENT_INTERACTION = "PATIENT_INTERACTION",
  ROUND_ROBIN = "ROUND_ROBIN",
  NONE = "NONE",
}

export interface TaskSetting {
  suppressTaskNotifications: boolean;
  completedTaskRetentionDays: number;
  defaultTaskFilter: TaskDefaultFilter;
  assignmentStrategy: TaskAssignmentStrategy;
  respectShifts: boolean;
}

export interface TaskSettingUpdate {
  suppressTaskNotifications?: boolean;
  completedTaskRetentionDays?: number;
  defaultTaskFilter?: TaskDefaultFilter;
  assignmentStrategy?: TaskAssignmentStrategy;
  respectShifts?: boolean;
}

export interface AlertTaskConfig {
  id: string;
  alertType: string;
  enabled: boolean;
  priority: TaskPriority;
  team?: Team;
  assignee?: ProviderTiny;
  assignToOrderingProvider?: boolean;
}

export interface AlertTaskConfigUpsert {
  alertType: string;
  enabled: boolean;
  priority: TaskPriority;
  teamId?: string | null;
  assigneeId?: string | null;
  assignToOrderingProvider?: boolean;
}

export interface AlertTaskConfigBulkUpdate {
  configs: AlertTaskConfigUpsert[];
}
